import React, { useCallback, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  InstrumentationServiceContext,
} from '@bingads-webui-campaign-react/smart-page-context';

import { BingMapsContext } from './bing-maps-context';
import {
  DEFAULT_BING_MAP_API_KEY,
  injectScript,
  pollForBingMapsSdk,
} from './utils';

/**
 * Provides shared values for initializing Bing Maps related services.
 * @param {Object} props - React Props.
 * @returns {ReactNode} - BingMapsContext provider.
 */
export function BingMapsContextProvider(props) {
  const {
    bingMapsApiKey,
    children,
  } = props;

  const [loadBingMapsSdkRequested, setLoadBingMapsSdkRequested] = useState(false);
  const [Maps, setMaps] = useState(null);

  const logger = useContext(InstrumentationServiceContext);

  const loadBingMapsSdk = useCallback(() => {
    if (loadBingMapsSdkRequested) {
      // SDK initialization has already started, nothing to do
      return;
    }

    setLoadBingMapsSdkRequested(true);

    // poll once to check if SDK is already on the page
    pollForBingMapsSdk(1).then((existingSdk) => {
      if (existingSdk) {
        setMaps(existingSdk);
      } else {
        // asynchronously load the external javascript and then poll until the Map APIs are loaded
        injectScript(logger)
          .then(pollForBingMapsSdk)
          .then((newSdk) => {
            setMaps(newSdk);
          });
      }
    });
  }, [loadBingMapsSdkRequested, logger]);

  const value = useMemo(() => ({
    bingMapsApiKey,
    loadBingMapsSdk,
    Maps,
  }), [
    bingMapsApiKey,
    loadBingMapsSdk,
    Maps,
  ]);

  return (
    <BingMapsContext.Provider value={value}>
      {children}
    </BingMapsContext.Provider>
  );
}

BingMapsContextProvider.propTypes = {
  /**
   * API key for use with Bing Maps. You can obtain an API key from https://www.bingmapsportal.com/
   */
  bingMapsApiKey: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
};

BingMapsContextProvider.defaultProps = {
  bingMapsApiKey: DEFAULT_BING_MAP_API_KEY,
};
